/**
 * iPIDiagram Validation Schema
 */

import { z } from 'zod';
import { evaluateExpression, parseCSVLine, validateCSVFormat } from '../shared';

export const baseSchema = z.object({
  project: z.string().min(1, 'Project name is required'),
  title: z.string().min(1, 'Title is required'),
  length: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n != null && n > 0;
  }, 'Length must be a positive number'),
  tribWidth: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n != null && n > 0;
  }, 'Tributary width must be a positive number'),
  hingeLength: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n != null && n > 0;
  }, 'Hinge length must be a positive number'),
  totalWeight: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n != null && n > 0;
  }, 'Total weight must be a positive number'),
  klmFactor: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n != null && n > 0 && n <= 1;
  }, 'KLM factor must be a number between 0 and 1'),
  Ru: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n != null && n > 0;
  }, 'Ru must be a positive number'),
  Xe: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n != null && n > 0;
  }, 'Xe must be a positive number'),
  loadType: z.string().refine(val => ['0', '1', '2'].includes(val), { message: 'Load type must be 0, 1, or 2' }),
  Pa: z.string().superRefine((val, ctx) => {
    const err = validateCSVFormat(val);
    if (err) ctx.addIssue({ code: 'custom', message: err });
  }),
  Ia: z.string().superRefine((val, ctx) => {
    const err = validateCSVFormat(val);
    if (err) ctx.addIssue({ code: 'custom', message: err });
  }),
  ductilityLimit: z.string().refine(val => validateCSVFormat(val) === null, { message: 'Ductility limit must be a valid CSV string' }),
  rotationLimit: z.string().refine(val => validateCSVFormat(val) === null, { message: 'Ductility limit must be a valid CSV string' })
});

/**
 * Full schema with cross-field validation
 */
export const schema = baseSchema.superRefine((data, ctx) => {
  if (parseCSVLine(data.Pa)?.length !== parseCSVLine(data.Ia)?.length) {
    const msg = 'Pressure and Impulse  must have the same number of values.';
    ctx.addIssue({ code: 'custom', message: msg, path: ['Pa'] });
    ctx.addIssue({ code: 'custom', message: msg, path: ['Ia'] });
  }
  if (parseCSVLine(data.ductilityLimit)?.length !== parseCSVLine(data.rotationLimit)?.length) {
    const msg = 'Ductility limit and Rotation limit must have the same number of values.';
    ctx.addIssue({ code: 'custom', message: msg, path: ['ductilityLimit'] });
    ctx.addIssue({ code: 'custom', message: msg, path: ['rotationLimit'] });
  }

  const hingeLength = evaluateExpression(data.hingeLength);
  const length = evaluateExpression(data.length);
  if (hingeLength != null && length != null && hingeLength > length) {
    const msg = `Hinge length ${hingeLength} cannot be greater than the length of the element ${length}.`;
    ctx.addIssue({ code: 'custom', message: msg, path: ['hingeLength'] });
  }
});
/**
 * Helper types
 */
export type FieldName = keyof z.infer<typeof baseSchema>;
export type FormValues = z.input<typeof schema>; // raw form shape (strings from inputs/selects)
export type ParsedValues = z.output<typeof schema>; // parsed shape (numbers after coercion)
export type FormErrors = Partial<Record<FieldName, string | null>>;

/**
 * Validate a single field
 */
export function validateOneField<K extends FieldName>(key: K, raw: unknown): string | null {
  const fieldSchema = baseSchema.shape[key];
  const res = fieldSchema.safeParse(raw);
  return res.success ? null : (res.error.issues[0]?.message ?? 'Invalid value');
}
